














import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import { EscalatedAlert, EscalatedAlertLog, EscalatedAlertStatus, EventType, Product, Vat } from '@/api';
import { EscalatedAlertApiService } from '@/services/escalated-alert-service';
import Toast from '@/components/display/toast.vue';
import VatChartAdmin from '@/components/charts/vat-chart-admin.vue';
import TooltipButton from '@/components/elements/buttons/tooltip-button.vue';
import StatusChangeModal from './status-change-modal.vue';
import { VatService } from '@/pages/account/products/milk/vats/service/vat-service';
import EscalatedAlertCardHeader from './escalated-alert-card-header.vue';
import InformationRow from '@/components/display/information-row.vue';
import LinkRow from '@/components/display/information-rows/link-row.vue';
import EscalatedAlertInfoPanel from './escalated-alert-info-panel.vue';
import EscalatedAlertCardTabs from './escalated-alert-card-tabs.vue';

@Component({
	components: {
		LinkRow,
		TooltipButton,
		VatChartAdmin,
		InformationRow,
		StatusChangeModal,
		EscalatedAlertCardTabs,
		EscalatedAlertInfoPanel,
		EscalatedAlertCardHeader,
	},
})
export default class EscalatedAlertCard extends Vue {
	@Prop({ type: Object })
	public alert?: null | EscalatedAlert;

	public tabIndex: number = 0;
	public saveVolume: number | null = null;

	// API fields

	public readonly toast: Toast = new Toast();
	public alertLogs?: EscalatedAlertLog[] = [];
	public readonly vatService: VatService = new VatService();
	public readonly escalatedAlertApi: EscalatedAlertApiService = new EscalatedAlertApiService();

	// API result fields

	public vat: Vat | null = null;
	public event: EventType | null = null;
	public product: Product | null = null;

	// API state fields

	public loadingProduct: boolean = true;
	public loadingEscalatedAlert: boolean = true;

	// Temperature arrow fields

	public currentTemp: number | null = null;
	public isTempGoingUp: boolean = false;
	public latestLogTemp: number | null = null;
	public tempIsAvailable: boolean = false;

	// Getters - For rerenders based on properties

	public get alertStatus(): string {
		return this.alert?.status ?? '';
	}

	public get totalRows(): number {
		return this.alertLogs?.length ?? 0;
	}

	@Emit('update')
	public emitUpdate() { }

	public mounted() {
		if (this.alert) {
			this.getAlertExtended();
		}
	}

	public async getVat(vatId: number) {
		const { vat, errorMessage } = await this.vatService.getVatById(vatId, {
			params: { include: 'currentEntityState' },
		});

		if (vat) {
			this.vat = vat;
			const metrics = JSON.parse(vat.currententitystate.metrics);
			this.currentTemp = metrics.vatTemperature;

			this.saveVolume =
				new Date(this.alert?.created_at ?? '')?.getTime() > metrics.lastMilking.timestamp
					? metrics.vatVolume
					: metrics.lastMilking.volumeMilked;
		} else if (errorMessage) {
			this.toast.makeToast(errorMessage, 'Vat Current State Error', 'danger');
		}
	}

	// Derives the header temperature-trend arrow. Depends on both the vat's current
	// temperature (from getVat) and the alert logs (from getAlertExtended), so it must
	// run only after both have resolved.
	public computeTemperatureTrend() {
		const hasLogs = this.alertLogs && this.alertLogs.length > 0;
		this.latestLogTemp = hasLogs && this.alertLogs
			? Math.max(...this.alertLogs.map((log) => log.temperature ?? 0))
			: null;

		this.tempIsAvailable =
			this.currentTemp != null && this.latestLogTemp != null && this.alertStatus !== 'new';

		if (this.tempIsAvailable) {
			/* @ts-ignore tempIsAvailable already checks that latestTemp and latestLogTemp undefined which ts cannot infer that. Could recheck but that's redundant */
			this.isTempGoingUp = this.currentTemp > parseFloat(this.latestLogTemp);
		}
	}

	@Watch('alert')
	public async getAlertExtended() {
		if (typeof this.alert?.id == 'number') {
			this.loadingEscalatedAlert = true;

			// The vat fetch gates the graph and only needs the entity id, which is
			// already present on the base alert row. Start it immediately, concurrently
			// with the (heavier, include-laden) detail fetch, rather than waiting for
			// the detail response before firing it.
			const vatPromise =
				typeof this.alert.entity_id === 'number'
					? this.getVat(this.alert.entity_id)
					: Promise.resolve();

			const { escalatedAlert, errorMessage } = await this.escalatedAlertApi.getEscalatedAlertById(
				this.alert.id,
				{
					params: { include: 'logs.user, event, entity, product, product.users' },
				},
			);

			if (escalatedAlert != null) {
				// Sorts logs by created at. If created at is undefined uses max safe int to put it to the end of the array
				this.alertLogs = escalatedAlert.logs?.sort((a: EscalatedAlertLog, b: EscalatedAlertLog) => {
					return (
						new Date(b.created_at ?? Number.MAX_SAFE_INTEGER).getTime() -
						new Date(a.created_at ?? Number.MAX_SAFE_INTEGER).getTime()
					);
				});
				this.tabIndex = escalatedAlert.status == EscalatedAlertStatus.Resolved ? 1 : 0

				this.event = escalatedAlert.event;
				this.product = escalatedAlert.product;
			} else if (errorMessage) {
				this.toast.makeToast(errorMessage, 'Escalated Alerts Error', 'danger');
			}
			this.loadingEscalatedAlert = false;

			// The temperature-trend arrow needs both the vat (currentTemp) and the logs,
			// so compute it only once both fetches have resolved.
			await vatPromise;
			this.computeTemperatureTrend();
		}
	}
}
